import React, { useState, useRef, useEffect } from "react";
import { HighlightOutlined } from "@ant-design/icons";
import { getHeroIcon } from "../../_helpers/heroicons";
import styles from "../styles/QurealRichEditor.module.css";

// Highlight color options
const HIGHLIGHT_COLORS = {
    yellow: "#FFEEBA",
    green: "#C3E6CB",
    blue: "#BEE5EB",
    pink: "#F5C2C7",
    orange: "#FFD8A8",
    purple: "#D6D8FC",
    red: "#F8D7DA",
};

const HighlightDropdown = ({ editor }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Handle click outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    // Check if highlight is active and get current color
    const getCurrentHighlightColor = () => {
        if (editor.isActive("highlight")) {
            const attrs = editor.getAttributes("highlight");
            // Check if color is present in attributes
            if (attrs && attrs.color) {
                return attrs.color;
            }
        }
        return null;
    };

    const currentColor = getCurrentHighlightColor();

    // Apply highlight with selected color
    const applyHighlight = (color) => {
        if (editor.isActive("highlight")) {
            editor.chain().focus().unsetHighlight().run();
        }
        editor.chain().focus().setHighlight({ color }).run();
        setIsOpen(false);
    };

    // Remove highlight
    const removeHighlight = () => {
        editor.chain().focus().unsetHighlight().run();
        setIsOpen(false);
    };

    return (
        <div className={styles.dropdown} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`${styles.button} ${styles.dropdownToggle} ${
                    editor.isActive("highlight") ? styles.isActive : ""
                }`}
                title="Highlight text"
            >
                <div className={styles.highlightButtonContainer}>
                    <span className={styles.dropdownLabel}>
                        <HighlightOutlined />
                    </span>

                    {/* Color indicator bar */}
                    <div
                        className={styles.colorIndicatorBar}
                        style={
                            currentColor
                                ? { backgroundColor: currentColor }
                                : {}
                        }
                    ></div>
                </div>

                {getHeroIcon("chevron-down", { width: "12px" })}
            </button>

            {isOpen && (
                <div className={styles.dropdownMenu}>
                    {/* Color options */}
                    <div className={styles.colorGrid}>
                        {Object.entries(HIGHLIGHT_COLORS).map(
                            ([name, color]) => (
                                <button
                                    key={name}
                                    onClick={() => applyHighlight(color)}
                                    className={`${styles.colorOption} ${
                                        currentColor === color
                                            ? styles.activeColorOption
                                            : ""
                                    }`}
                                    style={{ backgroundColor: color }}
                                    title={`${name} highlight`}
                                >
                                    {currentColor === color && (
                                        <span className={styles.colorCheckmark}>
                                            {getHeroIcon("check", {
                                                width: "14px",
                                            })}
                                        </span>
                                    )}
                                </button>
                            )
                        )}
                    </div>

                    {/* Remove highlight option */}
                    <button
                        onClick={removeHighlight}
                        className={styles.dropdownItem}
                        disabled={!editor.isActive("highlight")}
                    >
                        {getHeroIcon("x-mark", { width: "16px" })}
                        <span>Remove highlight</span>
                    </button>
                </div>
            )}
        </div>
    );
};

export default HighlightDropdown;
